/**
 * Encerramento do sistema por cliente (hostname).
 *
 * COMO OPERAR
 * - Prorrogar o prazo: altere ULTIMO_DIA_DE_ACESSO (vale para todos) ou o
 *   `ultimoDiaDeAcesso` de um cliente específico. Formato AAAA-MM-DD, horário de
 *   Brasília. O bloqueio começa às 00:00 do dia seguinte a essa data.
 *   Publique com 24–48h de antecedência (o index.html pode ficar em cache).
 * - Voltar a permitir o login: `bloquearLogin: false` no cliente. Após o prazo,
 *   isso libera o sistema SEM aviso; para manter o aviso, prorrogue a data.
 * - Liberação temporária após o prazo: `liberacaoTemporaria` no cliente, com
 *   `inicio` (AAAA-MM-DDTHH:mm, horário de Brasília) e `duracaoHoras`. Dentro
 *   dessa janela o sistema volta a funcionar, com um timer regressivo na barra
 *   e no comunicado. Antes e depois dela, o acesso continua encerrado. Por isso
 *   dá para publicar antes do horário de início. Para prorrogar, altere
 *   `inicio`/`duracaoHoras` e publique antes do fim da janela.
 * - Desligar tudo (aviso e bloqueio) de uma vez: ENCERRAMENTO_ATIVO = false.
 * - Validar no localhost: o aviso aparece normalmente e o bloqueio só ocorre
 *   com uma data simulada. Crie `.env.development.local` com
 *   REACT_APP_ENCERRAMENTO_DATA_SIMULADA=2026-11-03 (ou 2026-11-02T23:59 para ver
 *   a virada da meia-noite) e reinicie o `yarn start`. Simular o bloqueio limpa
 *   todo o localStorage do localhost. Com hora, o relógio simulado continua entre
 *   recarregamentos até o bloqueio; depois dele, recomeça na hora informada.
 *   O localhost usa a mesma liberação temporária do supergemaravilhasfestas:
 *   2026-11-05T15:13 mostra o timer acabando e 2026-11-04T15:13 mostra a
 *   liberação começando (cerca de 1 minuto após as 15:15, sem recarregar).
 */

export const ENCERRAMENTO_ATIVO = true;

const ULTIMO_DIA_DE_ACESSO = '2026-11-02';

interface LiberacaoTemporaria {
  /** Início da liberação (AAAA-MM-DDTHH:mm, horário de Brasília). */
  inicio: string;
  /** Duração da liberação em horas, contada a partir do início. */
  duracaoHoras: number;
}

interface ConfiguracaoEncerramento {
  /** Último dia com acesso permitido (AAAA-MM-DD, horário de Brasília). */
  ultimoDiaDeAcesso: string;
  /** Bloqueia o acesso a partir do dia seguinte ao último dia. */
  bloquearLogin: boolean;
  /** Janela em que o acesso volta a ser permitido após o prazo. */
  liberacaoTemporaria?: LiberacaoTemporaria;
}

const LIBERACAO_SUPERGEMA: LiberacaoTemporaria = {
  inicio: '2026-11-04T15:15',
  duracaoHoras: 24,
};

const CLIENTES_EM_ENCERRAMENTO: Record<string, ConfiguracaoEncerramento> = {
  'rubervalcarvalhodeandrade.gestao.app.konvix.com.br': {
    ultimoDiaDeAcesso: ULTIMO_DIA_DE_ACESSO,
    bloquearLogin: true,
  },
  'supergemaravilhasfestas.gestao.app.konvix.com.br': {
    ultimoDiaDeAcesso: ULTIMO_DIA_DE_ACESSO,
    bloquearLogin: true,
    liberacaoTemporaria: LIBERACAO_SUPERGEMA,
  },
};

/** Exibe o aviso no ambiente local para validação (o bloqueio local só ocorre com data simulada). */
const EXIBIR_NO_LOCALHOST = true;

const HOSTS_LOCAIS = new Set(['localhost', '127.0.0.1', '[::1]']);

export const CONTATO_ENCERRAMENTO = {
  nome: 'Alex Fiaiz',
  empresa: 'SoftTec Sistemas',
  telefone: '+55 (62) 9934-7909',
  linkWhatsapp: 'https://wa.me/556299347909',
};

export type StatusEncerramento =
  | { situacao: 'inativo' }
  | {
      situacao: 'aviso';
      ultimoDiaDeAcesso: string;
      diasAteUltimoDia: number;
      hoje: string;
    }
  | {
      situacao: 'liberacaoTemporaria';
      ultimoDiaDeAcesso: string;
      inicioLiberacao: number;
      fimLiberacao: number;
    }
  | {
      situacao: 'encerrado';
      ultimoDiaDeAcesso: string;
      /** Fim da liberação temporária, quando ela já terminou. */
      fimLiberacao?: number;
    };

const INATIVO: StatusEncerramento = { situacao: 'inativo' };

const UMA_HORA_EM_MS = 60 * 60 * 1000;
const UM_DIA_EM_MS = 24 * UMA_HORA_EM_MS;
const TRES_HORAS_EM_MS = 3 * 60 * 60 * 1000;
const FORMATO_DATA = /^(\d{4})-(\d{2})-(\d{2})$/;
const FORMATO_DATA_SIMULADA = /^(\d{4}-\d{2}-\d{2})(?:T(\d{2}):(\d{2}))?$/;

const DIAS_DA_SEMANA = [
  'domingo',
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado',
];

const obterHostname = (): string =>
  typeof window === 'undefined' ? '' : window.location.hostname.toLowerCase();

const ehHostLocal = (): boolean => HOSTS_LOCAIS.has(obterHostname());

/** Converte 'AAAA-MM-DD' no início do dia em UTC (ms); NaN se a data for inválida. */
const paraDiaUTC = (data: string): number => {
  const partes = FORMATO_DATA.exec(data);
  if (!partes) return NaN;

  const ano = Number(partes[1]);
  const mes = Number(partes[2]) - 1;
  const dia = Number(partes[3]);
  const valor = Date.UTC(ano, mes, dia);
  const conferencia = new Date(valor);

  return conferencia.getUTCFullYear() === ano &&
    conferencia.getUTCMonth() === mes &&
    conferencia.getUTCDate() === dia
    ? valor
    : NaN;
};

/**
 * Converte 'AAAA-MM-DD' ou 'AAAA-MM-DDTHH:mm' (horário de Brasília) em instante
 * (ms); NaN se for inválido.
 */
const paraInstanteBrasilia = (valor: string): number => {
  const partes = FORMATO_DATA_SIMULADA.exec(valor);
  const dia = partes ? paraDiaUTC(partes[1]) : NaN;
  const horas = Number(partes?.[2] || 0);
  const minutos = Number(partes?.[3] || 0);

  if (Number.isNaN(dia) || horas > 23 || minutos > 59) return NaN;

  // 00:00 em Brasília (UTC−3) corresponde às 03:00 UTC
  return dia + (horas * 60 + minutos) * 60 * 1000 + TRES_HORAS_EM_MS;
};

const DATA_SIMULADA = ehHostLocal()
  ? (process.env.REACT_APP_ENCERRAMENTO_DATA_SIMULADA || '').trim()
  : '';

const CHAVE_INICIO_SIMULACAO = 'KONVIX_ENCERRAMENTO_INICIO_SIMULACAO';

/** Instante inicial da data simulada (somente localhost); NaN quando não há simulação válida. */
const obterInicioSimulado = (valor: string): number => {
  if (!valor) return NaN;

  const inicio = paraInstanteBrasilia(valor);

  if (Number.isNaN(inicio)) {
    console.error(
      `[Encerramento] REACT_APP_ENCERRAMENTO_DATA_SIMULADA inválida ("${valor}"), use AAAA-MM-DD ou AAAA-MM-DDTHH:mm. Simulação ignorada.`,
    );
  }

  return inicio;
};

/**
 * Mantém o relógio simulado entre recarregamentos da aba (como o recarregamento
 * automático na virada do prazo). Ao bloquear, o useAuth limpa o sessionStorage
 * e a simulação recomeça no próximo carregamento.
 */
const ancorarRelogioSimulado = (valor: string): number => {
  const agoraReal = Date.now();

  try {
    const [valorSalvo, inicioSalvo] = (
      sessionStorage.getItem(CHAVE_INICIO_SIMULACAO) || ''
    ).split('|');

    if (valorSalvo === valor && Number(inicioSalvo) > 0) {
      return Number(inicioSalvo);
    }

    sessionStorage.setItem(CHAVE_INICIO_SIMULACAO, `${valor}|${agoraReal}`);
  } catch {
    // sem storage: o relógio simulado recomeça a cada carregamento
  }

  return agoraReal;
};

const INICIO_SIMULADO = obterInicioSimulado(DATA_SIMULADA);
const INICIO_REAL_DA_SIMULACAO = Number.isNaN(INICIO_SIMULADO)
  ? 0
  : ancorarRelogioSimulado(DATA_SIMULADA);

/** Data/hora atual (ms), avançando a partir da data simulada quando houver. */
const agora = (): number =>
  Number.isNaN(INICIO_SIMULADO)
    ? Date.now()
    : INICIO_SIMULADO + (Date.now() - INICIO_REAL_DA_SIMULACAO);

/** Data 'AAAA-MM-DD' do instante no horário de Brasília, independente do fuso do computador. */
const dataEmBrasilia = (instante: number): string => {
  try {
    const partes = new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date(instante));
    const parte = (tipo: string) => partes.find((p) => p.type === tipo)?.value;
    const data = `${parte('year')}-${parte('month')}-${parte('day')}`;

    if (FORMATO_DATA.test(data)) return data;
  } catch {
    // navegador sem suporte ao fuso: segue para o cálculo fixo em UTC−3
  }

  return new Date(instante - TRES_HORAS_EM_MS).toISOString().slice(0, 10);
};

const obterConfiguracao = (): ConfiguracaoEncerramento | undefined => {
  const hostname = obterHostname();

  if (
    Object.prototype.hasOwnProperty.call(CLIENTES_EM_ENCERRAMENTO, hostname)
  ) {
    return CLIENTES_EM_ENCERRAMENTO[hostname];
  }

  if (EXIBIR_NO_LOCALHOST && HOSTS_LOCAIS.has(hostname)) {
    return {
      ultimoDiaDeAcesso: ULTIMO_DIA_DE_ACESSO,
      bloquearLogin: true,
      liberacaoTemporaria: LIBERACAO_SUPERGEMA,
    };
  }

  return undefined;
};

interface JanelaLiberacao {
  inicio: number;
  fim: number;
}

/** Início e fim (ms) da liberação temporária; undefined se não houver ou se for inválida. */
const obterJanelaLiberacao = (
  liberacao?: LiberacaoTemporaria,
): JanelaLiberacao | undefined => {
  if (!liberacao) return undefined;

  const inicio = paraInstanteBrasilia(liberacao.inicio);
  const { duracaoHoras } = liberacao;

  if (
    Number.isNaN(inicio) ||
    !Number.isFinite(duracaoHoras) ||
    duracaoHoras <= 0
  ) {
    console.error(
      `[Encerramento] liberacaoTemporaria inválida (inicio "${liberacao.inicio}", duracaoHoras ${duracaoHoras}), use inicio AAAA-MM-DDTHH:mm e duracaoHoras > 0. Liberação ignorada.`,
    );
    return undefined;
  }

  return { inicio, fim: inicio + duracaoHoras * UMA_HORA_EM_MS };
};

/** Milissegundos que faltam até o instante informado (nunca negativo). */
export const msRestantes = (instante: number): number =>
  Math.max(0, instante - agora());

/** 'AAAA-MM-DD' → 'DD/MM/AAAA' */
export const formatarData = (data: string): string => {
  const partes = FORMATO_DATA.exec(data);
  return partes ? `${partes[3]}/${partes[2]}/${partes[1]}` : data;
};

/** 'AAAA-MM-DD' → 'segunda-feira' */
export const diaDaSemana = (data: string): string => {
  const valor = paraDiaUTC(data);
  return Number.isNaN(valor) ? '' : DIAS_DA_SEMANA[new Date(valor).getUTCDay()];
};

/** 'AAAA-MM-DD' → 'AAAA-MM-DD' do dia seguinte */
export const diaSeguinte = (data: string): string => {
  const valor = paraDiaUTC(data);
  return Number.isNaN(valor)
    ? data
    : new Date(valor + UM_DIA_EM_MS).toISOString().slice(0, 10);
};

/**
 * Instante → 'DD/MM/AAAA às HH:mm' (ou 'quinta-feira, DD/MM/AAAA às HH:mm'),
 * no horário de Brasília. Usa UTC−3 fixo, o mesmo de `paraInstanteBrasilia`,
 * para exibir exatamente o horário configurado.
 */
export const formatarDataHora = (
  instante: number,
  comDiaDaSemana = false,
): string => {
  const local = new Date(instante - TRES_HORAS_EM_MS).toISOString();
  const data = local.slice(0, 10);
  const texto = `${formatarData(data)} às ${local.slice(11, 16)}`;

  return comDiaDaSemana ? `${diaDaSemana(data)}, ${texto}` : texto;
};

/**
 * Situação do encerramento para o host atual. Nunca lança exceção: o resultado
 * é usado na raiz da aplicação (App.tsx) e no useAuth.
 */
export const obterStatusEncerramento = (): StatusEncerramento => {
  const configuracao = ENCERRAMENTO_ATIVO ? obterConfiguracao() : undefined;
  if (!configuracao) return INATIVO;

  try {
    const { ultimoDiaDeAcesso, bloquearLogin } = configuracao;
    const ultimoDia = paraDiaUTC(ultimoDiaDeAcesso);

    if (Number.isNaN(ultimoDia)) {
      console.error(
        `[Encerramento] ultimoDiaDeAcesso inválido ("${ultimoDiaDeAcesso}"), use AAAA-MM-DD.`,
      );
      return INATIVO;
    }

    const instanteAtual = agora();
    const hoje = dataEmBrasilia(instanteAtual);
    const diasAteUltimoDia = Math.round(
      (ultimoDia - paraDiaUTC(hoje)) / UM_DIA_EM_MS,
    );

    if (diasAteUltimoDia >= 0) {
      return { situacao: 'aviso', ultimoDiaDeAcesso, diasAteUltimoDia, hoje };
    }

    const liberacao = obterJanelaLiberacao(configuracao.liberacaoTemporaria);

    if (
      liberacao &&
      instanteAtual >= liberacao.inicio &&
      instanteAtual < liberacao.fim
    ) {
      return {
        situacao: 'liberacaoTemporaria',
        ultimoDiaDeAcesso,
        inicioLiberacao: liberacao.inicio,
        fimLiberacao: liberacao.fim,
      };
    }

    const podeBloquearNesteHost =
      !ehHostLocal() || !Number.isNaN(INICIO_SIMULADO);

    if (bloquearLogin && podeBloquearNesteHost) {
      return liberacao && instanteAtual >= liberacao.fim
        ? {
            situacao: 'encerrado',
            ultimoDiaDeAcesso,
            fimLiberacao: liberacao.fim,
          }
        : { situacao: 'encerrado', ultimoDiaDeAcesso };
    }

    return INATIVO;
  } catch (erro) {
    console.error('[Encerramento] Falha ao calcular a situação.', erro);
    return INATIVO;
  }
};

export const acessoEncerrado = (): boolean =>
  obterStatusEncerramento().situacao === 'encerrado';
